import Cookies from 'js-cookie';

import type {
  MobileCookies,
  GtmStartEventData,
  ForwardToChannelMethod,
  GtmModel,
  LoginEventData,
  RegisterConsultantEventData,
  RegisteredUserEventData,
  UpgradedUserEventData,
} from '~/models';
import {
  cookieNames,
  GTM_START_EVENT,
  GTM_START,
  LOGIN_EVENT,
  REGISTERED_CONSULTANT_EVENT,
  REGISTERED_USER_EVENT,
  UPGRADED_USER_EVENT,
} from '~/models';
import { domReadyPromise } from '~/utils';

declare global {
  interface Window {
    dataLayer?: object[];
    gtag?: (...params: object[]) => void;
  }
}

const GOOGLE_TAG_MANAGER_PATH = '//www.googletagmanager.com/gtm.js?id=';

let model: GtmModel;

function createDataLayer() {
  if (typeof window.dataLayer === 'undefined') {
    window.dataLayer = [];
  }
  if (typeof window.gtag === 'undefined') {
    window.gtag = (...params) => window.dataLayer?.push(...params);
  }
}

function createGoogleTagManagerScript() {
  const script = document.createElement('script');
  script.async = true;
  script.src = GOOGLE_TAG_MANAGER_PATH + model.GtmId;
  document.head.insertAdjacentElement('afterbegin', script);
}

function pushCustomerUpgradedEvent() {
  const data: UpgradedUserEventData = {
    customerNewType: model.CustomerTypeIdAfterUpgrade ?? null,
    customerOldType: model.CustomerTypeIdBeforeUpgrade ?? null,
    event: UPGRADED_USER_EVENT,
    upgradedUserId: model.ConsultantId,
  };

  window.dataLayer?.push(data);
}

function pushGtmStartEvent() {
  const data: GtmStartEventData = {
    consultantId: model.ConsultantId,
    customerId: model.CustomerId,
    customerTypeId: model.CustomerTypeId,
    event: GTM_START_EVENT,
    [GTM_START]: Date.now(),
    periodId: model.PeriodId,
    signUpDate: model.SignUpDate ?? '',
    visitorSegment: model.VisitorSegment,
  };

  window.dataLayer?.push(data);
}

function pushLoginEvent() {
  const data: LoginEventData = {
    consultantId: model.ConsultantId,
    event: LOGIN_EVENT,
    loginType: model.LoginType ?? null,
  };

  window.dataLayer?.push(data);
}

function pushMobileCookies() {
  const data: MobileCookies = {};

  cookieNames.forEach((cookieName) => {
    const tempCookie = Cookies.get(cookieName);

    if (tempCookie) {
      data[cookieName] = tempCookie;
    }
  });

  if (Object.keys(data).length) {
    window.dataLayer?.push(data);
  }
}

function pushRegisteredConsultantEvent() {
  const data: RegisterConsultantEventData = {
    consultantId: model.ConsultantId,
    event: REGISTERED_CONSULTANT_EVENT,
    registeredConsultantId: model.RegisteredConsultantNumber ?? '',
    registrationType: model.RegistrationType ?? '',
    sponsorNumber: model.SponsorNumber ?? '',
  };

  window.dataLayer?.push(data);
}

function pushRegisteredUserEvent() {
  const data: RegisteredUserEventData = {
    customerTypeId: model.RegisteredCustomerTypeId ?? 0,
    event: REGISTERED_USER_EVENT,
    registeredUserId: model.RegisteredConsultantNumber ?? '',
    registrationChannel: model.RegistrationChannel ?? '',
    registrationType: model.RegistrationType ?? '',
    sponsorNumber: model.SponsorNumber ?? '',
    signUpDate: model.RegistrationSingUpDate,
    visitorSegment: model.RegistrationVisitorSegment,
  };

  window.dataLayer?.push(data);
}

function pushBasicUserIdentificationEvents() {
  if (model.IsLoggedInEvent) {
    domReadyPromise.then(pushLoginEvent, undefined);
  }
  if (model.IsConsultantRegistrationEvent) {
    domReadyPromise.then(pushRegisteredConsultantEvent, undefined);
  }
  if (model.IsCustomerRegistrationEvent) {
    domReadyPromise.then(pushRegisteredUserEvent, undefined);
  }
  if (model.IsCustomerUpgradeEvent) {
    domReadyPromise.then(pushCustomerUpgradedEvent, undefined);
  }
}

export function init(data: GtmModel) {
  model = data;
  createDataLayer();
  createGoogleTagManagerScript();
  pushGtmStartEvent();
  pushMobileCookies();
  pushBasicUserIdentificationEvents();
}

export const forwardEvent: ForwardToChannelMethod = (event) => {
  const data = {
    event: event.detail.originalEventType,
    ...event.detail.originalEventDetail,
  };

  window.dataLayer?.push(data);
};

export function handleAnalyticsEventsFromPartialView(data: GtmModel) {
  model = data;
  pushBasicUserIdentificationEvents();
}
